"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { UiFeedbackProvider } from "@/components/ui/UiFeedback";
import { getApiUrl, readApiJson } from "@/lib/utils";

export type UserRole = "admin" | "super_user" | "end_user";

export type User = {
  id: number;
  name: string;
  email: string;
  department_id: number | null;
  department: { id: number; name: string } | null;
  role: UserRole;
  is_admin: boolean;
  is_super_user?: boolean;
  is_end_user?: boolean;
  can_manage_roles?: boolean;
  can_edit_assets?: boolean;
  can_edit_masters?: boolean;
};

type AuthContextValue = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  fetchWithAuth: (url: string, init?: RequestInit) => Promise<Response>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchWithAuth = useCallback(async (url: string, init?: RequestInit) => {
    const t = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const headers = new Headers(init?.headers);
    if (t) headers.set("Authorization", `Bearer ${t}`);
    if (init?.body instanceof FormData) {
      headers.delete("Content-Type");
    }
    return fetch(url, { ...init, headers });
  }, []);

  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) {
      setLoading(false);
      return;
    }
    setToken(t);
    fetch(`${getApiUrl()}/api/me`, {
      headers: { Authorization: `Bearer ${t}`, Accept: "application/json" },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("unauthorized");
        return readApiJson<User>(r);
      })
      .then(setUser)
      .catch(() => {
        localStorage.removeItem("token");
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${getApiUrl()}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await readApiJson<{ message?: string; token?: string; user?: User }>(res);
    if (!res.ok) {
      throw new Error(data.message || "เข้าสู่ระบบไม่สำเร็จ");
    }
    if (!data.token || !data.user) {
      throw new Error("เข้าสู่ระบบไม่สำเร็จ (ไม่มี token)");
    }
    localStorage.setItem("token", data.token);
    setToken(data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    const t = localStorage.getItem("token");
    if (t) {
      fetch(`${getApiUrl()}/api/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${t}`, Accept: "application/json" },
      }).catch(() => {});
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, fetchWithAuth }}>
      <UiFeedbackProvider>{children}</UiFeedbackProvider>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
